import { useCompanyCRUDActions } from '@mm/state/actions/CompanyCRUDActions';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import { OnboardingSignupStep } from '@mm/state/model/CompanyUsersModel';
import {
  isFacebookUserSelector,
  selectSessionSelector,
} from '@mm/state/selectors/userSelector';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { useMachine } from '@xstate/react';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Machine } from 'xstate';
import { SIGNUP } from '../../../../../common/helpers/plugins/constants';
import { trackFacebookEvent } from '../../../../../common/helpers/plugins/facebook-pixel-init';
import { trackSignUpEventGA } from '../../../../../modules/MarketingAnalytics';
import { signUpFlowMachineConfig } from '../../../../signUpFlowMachine/signUpFlowMachine';
import {
  SignUpFlowEvents,
  SignUpFlowEventsTypes,
} from '../../../../signUpFlowMachine/signUpMachineEventTypes';
import {
  SignUpFlowContext,
  SignUpFlowStatesSchema,
  SIGN_UP_FLOW_STATES_NAME,
} from '../../../../signUpFlowMachine/signUpMachineTypes';
import { NewBillingModal2023 } from '../../../XRayOnboarding/NewBillingModal2023/NewBillingModal2023';
import OnboardingPageLoadingIndicator from '../../common/OnboardingPageLoadingIndicator/OnboardingPageLoadingIndicator';
import { useSetNextOnboardingStep } from '../../use-set-next-onboarding-step';
import { WelcomePage } from './components/WelcomePage/WelcomePage';
import './NewUserInfoFlow.scss';

export const NewUserInfoFlow = () => {
  const activeCompany = useSelector(activeCompanySelector);
  const isFBUser = useSelector(isFacebookUserSelector);

  const { update } = useCompanyCRUDActions();
  const { setNextOnboardingStep } = useSetNextOnboardingStep();
  const { userAttributes, id: userId } = useSelector(selectSessionSelector);
  const { updateUserAttributes } = useUsersCRUDActions();

  const registerSignUpEvent = () => {
    trackFacebookEvent(SIGNUP);
    trackSignUpEventGA();
    setNextOnboardingStep(
      OnboardingSignupStep.NONE,
      false,
      isFBUser ? 'facebook' : undefined,
    );
  };

  const redirectService = useCallback(async (ctx: SignUpFlowContext) => {
    if (ctx.company?.id) {
      await update({
        id: ctx.company.id,
        goal: ctx.company.goal,
      });
    }
    registerSignUpEvent();
    return null;
  }, []);

  const updateCompanyService = useCallback(async (ctx: SignUpFlowContext) => {
    if (ctx.company && ctx.company?.id) {
      const {
        name,
        businessEmail,
        website,
        phoneNumber,
        jobRole,
        clientsCharge,
        goal,
      } = ctx.company;
      update({
        id: ctx.company.id,
        name,
        businessEmail,
        website,
        phoneNumber,
        jobRole,
        clientsCharge,
        goal,
      });

      if (ctx.company.id) {
        updateUserAttributes(ctx.company.id, userId, {
          onboardingCompanyInfoStep: ctx.activeStep,
        });
      }
    }
    return null;
  }, []);

  const signUpFlowOptions = useMemo(() => {
    return {
      services: {
        updateCompanyService,
        redirectService,
      },
    };
  }, []);

  const signUpFlowMachine = useMemo(() => {
    return Machine<SignUpFlowContext, SignUpFlowStatesSchema, SignUpFlowEvents>(
      signUpFlowMachineConfig,
      signUpFlowOptions,
    );
  }, [signUpFlowOptions]);

  const [current, send] = useMachine(signUpFlowMachine, {
    context: {
      company: activeCompany,
      activeStep: userAttributes.onboardingCompanyInfoStep ?? 0,
    },
    devTools: false,
  });

  const { company } = current.context;

  const isWelcomeScreenActive = current.matches(
    SIGN_UP_FLOW_STATES_NAME.WELCOME,
  );
  const isBillingScreenActive = current.matches(
    SIGN_UP_FLOW_STATES_NAME.BILLING,
  );

  const isRedirect = current.matches(SIGN_UP_FLOW_STATES_NAME.REDIRECT);

  const sendStartEvent = (
    name: string,
    businessEmail: string,
    website: string,
    phoneNumber: string,
    jobRole: string,
    clientsCharge: string,
    goal: string,
  ) => {
    send({
      type: SignUpFlowEventsTypes.START,
      data: {
        name,
        businessEmail,
        website,
        phoneNumber,
        jobRole,
        clientsCharge,
        goal,
      },
    });
  };
  useEffect(() => {
    // old users who stopped on any onboarding step
    if (
      current.context.activeStep > 1 &&
      userAttributes.onboardingSignupStep === OnboardingSignupStep.USER_INFO
    ) {
      registerSignUpEvent();
    }
  }, []);

  if (!company) {
    return null;
  }

  if (isRedirect) {
    return <></>;
  }

  return (
    <div className="user-info-flow">
      {isWelcomeScreenActive && (
        <WelcomePage onStartPress={sendStartEvent} company={company} />
      )}
      {isBillingScreenActive && (
        <NewBillingModal2023
          className="user-info-flow__billing_step"
          withCloseButton={false}
          withLogo
          withOverlay
          onClose={() => {
            setNextOnboardingStep(OnboardingSignupStep.INSTALLATION);
          }}
        />
      )}

      <OnboardingPageLoadingIndicator isLoading={false} />
    </div>
  );
};
